import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { t } from 'app/core/i18n/i18n';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import { Subject, debounceTime, distinctUntilChanged, filter, merge, skip, switchMap } from 'rxjs';
import { computeFrequencyData } from '../../helpers/frequency.helpers';
import { BusinessPartnerBySubRoute } from '../../interfaces/busines-partner-by-sub-route.interface';
import { AssignedPartnersPanelService } from '../assigned-partners-panel/assigned-partners-panel.service';
import {
  AssignedPartnersSubRouteFilters,
  AssignedPartnersSubRoutePagination,
} from '../sub-route-partners.types';

@Injectable()
export class AssignedPartnersListService {
  private businessPartnerService = inject(BusinessPartnerService);
  private panelService = inject(AssignedPartnersPanelService);
  private handlerMessage = inject(HandlerMessageService);

  // === STATE SIGNALS ===
  private _rawData = signal<BusinessPartnerBySubRoute[]>([]);
  private _totalItems = signal<number>(0);
  private _loading = signal<boolean>(false);
  private _pagination = signal<AssignedPartnersSubRoutePagination>({
    currentPage: 1,
    limitPerPage: 50,
    sort: 'name',
    sortDirection: 'ASC',
    search: '',
  });

  // === SHARED SIGNAL FOR SEARCH (from panel service) ===
  readonly searchTerm = this.panelService.searchTerm;

  // === ACTION SUBJECTS ===
  readonly updatePagination$ = new Subject<Partial<AssignedPartnersSubRoutePagination>>();
  readonly reload$ = new Subject<void>();

  // === PUBLIC COMPUTED ===
  // Data with computed frequency fields
  readonly data = computed<BusinessPartnerBySubRoute[]>(() =>
    this._rawData().map((item) => ({
      ...item,
      ...computeFrequencyData(item.frequenciesFromThisSubRoute),
    }))
  );
  readonly totalItems = computed(() => this._totalItems());
  readonly loading = computed(() => this._loading());
  readonly pagination = computed(() => this._pagination());

  // Usa el computed del panel que filtra por modo lista
  readonly subRouteId = this.panelService.subRouteIdForListView;

  // Expose subRouteData from panel service
  readonly subRouteData = this.panelService.subRouteData;

  // === OBSERVABLES FROM SIGNALS ===
  private subRouteId$ = toObservable(this.subRouteId);
  private searchTerm$ = toObservable(this.searchTerm);
  private pagination$ = toObservable(this._pagination);

  constructor() {
    // Search term with debounce - updates pagination
    this.searchTerm$
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed(), skip(1))
      .subscribe((search) => {
        this._pagination.update((prev) => ({ ...prev, search, currentPage: 1 }));
      });

    // Pagination updates (without search, which is handled separately)
    this.updatePagination$.pipe(takeUntilDestroyed()).subscribe((pagination) => {
      this._pagination.update((prev) => ({ ...prev, ...pagination }));
    });

    // Reactive data loading with switchMap for request cancellation
    // Triggers on: subRouteId change, pagination change, or manual reload
    merge(this.subRouteId$.pipe(filter((id) => !!id)), this.pagination$, this.reload$)
      .pipe(
        debounceTime(50), // Small debounce to batch rapid changes
        switchMap(() => {
          const subRouteId = this.panelService.subRouteIdForListView();
          if (!subRouteId) {
            return [];
          }

          this._loading.set(true);
          return this.businessPartnerService.findAssignedToSubRouteBusinessPartnerWithCount(
            {} as AssignedPartnersSubRouteFilters,
            this._pagination(),
            subRouteId,
            this.subRouteData()?.company?._id?.toString() ?? null
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (response) => {
          if (response && response.data) {
            this._rawData.set(response.data.data);
            // pageInfo es un array en la respuesta real
            const pageInfo = response.data.pageInfo as unknown as {
              _id: string | null;
              total: number;
            }[];
            this._totalItems.set(pageInfo[0]?.total ?? 0);
          }
          this._loading.set(false);
        },
        error: (err) => {
          this._loading.set(false);
          this.showLoadError(err);
        },
      });
  }

  private showLoadError(err: { error?: { message?: string } }): void {
    this.handlerMessage.error({
      message: err?.error?.message,
      alternativeMessage: t('routing.SUB_ROUTE_PARTNERS.ERRORS.LOAD_FAILED'),
    });
  }
}
